import { useCallback, useEffect, useMemo } from "react";

import { useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";

import { PricingSection } from "@/components/pricing/pricing-section";
import { useBrowsingCountry } from "@/lib/hooks/use-browsing-country";

import type { PricingTier } from "./checkout/checkout-types";

// Legacy source: src/components/billing/inline-plan-selector.tsx (1:1 port).
// Category tabs + full PricingSection, with the active category kept in the URL
// (?category=) exactly like legacy. Tiers are fetched from
// GET /api/pricing/tiers via react-query (legacy: useEffect+fetch).
//
// D2 (2026-09-20, `_docs/decizii-abonare-founding-2026-09-19.md` §2.5) adds the
// `facility` category — clinic plans priced "base + per covered doctor". The
// category id IS the `entityType` handed to `onSelectPlan`, so picking a clinic
// plan here checks out as entityType `facility`.

const CATEGORIES = [
  { id: "user" },
  { id: "provider" },
  { id: "facility" },
  { id: "business" },
] as const;

type CategoryId = (typeof CATEGORIES)[number]["id"];

interface TiersResponse {
  tiers?: PricingTier[];
  country?: {
    currency?: string;
    currencySymbol?: string;
    currencyPosition?: string;
    name?: string;
  };
}

interface InlinePlanSelectorProps {
  defaultCategory?: string;
  /** Hide pricing header, reduce spacing — for modal use */
  compact?: boolean;
  onSelectPlan: (
    planCode: string,
    planName: string,
    categoryType: string,
    planData: PricingTier,
    interval: "monthly" | "annual",
  ) => void;
}

export function InlinePlanSelector({
  defaultCategory = "user",
  compact,
  onSelectPlan,
}: InlinePlanSelectorProps) {
  const { t, i18n } = useLingui();
  const { country: browsingCountry } = useBrowsingCountry();
  const country = browsingCountry || "ro";
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;

  // Legacy: facility.planChange.category* keys.
  const categoryLabels: Record<CategoryId, string> = {
    user: t`User`,
    provider: t`Doctors & Clinics`,
    facility: t`Clinic`,
    business: t`Business`,
  };

  const rawCategory = typeof search.category === "string" ? (search.category as CategoryId) : null;
  const category =
    rawCategory && CATEGORIES.some((c) => c.id === rawCategory) ? rawCategory : defaultCategory;

  const setCategory = useCallback(
    (id: string) => {
      // Legacy kept only tab/subtab and set category (router.push, scroll: false).
      navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = {};
          if (prev.tab) next.tab = prev.tab;
          if (prev.subtab) next.subtab = prev.subtab;
          next.category = id;
          return next;
        },
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const tiersQuery = useQuery({
    queryKey: ["api", "/api/pricing/tiers", category, country, i18n.locale],
    queryFn: () =>
      apiFetch.get<TiersResponse>(
        `/api/pricing/tiers?targetType=${category}&country=${country}&locale=${i18n.locale}`,
      ),
  });
  const tiers = useMemo(() => tiersQuery.data?.tiers ?? [], [tiersQuery.data]);
  const countryData = tiersQuery.data?.country ?? {};
  const loading = tiersQuery.isPending;

  // Scroll to hash target (e.g. #Expert) after tiers load
  useEffect(() => {
    if (!loading && tiers.length > 0 && typeof window !== "undefined" && window.location.hash) {
      const id = decodeURIComponent(window.location.hash.slice(1));
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-primary/40", "ring-2");
          setTimeout(() => el.classList.remove("ring-primary/40", "ring-2"), 2000);
        }
      });
    }
  }, [loading, tiers]);

  const handleSelectPlan = useCallback(
    (planCode: string, interval: "monthly" | "annual") => {
      if (planCode.endsWith("_free")) return;
      const tier = tiers.find((t) => t.planCode === planCode);
      if (tier) {
        onSelectPlan(planCode, tier.name, category, tier, interval);
      }
    },
    [tiers, category, onSelectPlan],
  );

  return (
    <div className="space-y-4">
      {/* Category tabs — 4 audiences since D2, so the strip scrolls sideways at
          375px instead of squashing the labels. */}
      <div className="bg-muted flex gap-1 overflow-x-auto rounded-lg p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setCategory(cat.id)}
            className={`min-h-[44px] min-w-fit flex-1 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              category === cat.id
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground"
            }`}
          >
            {categoryLabels[cat.id]}
          </button>
        ))}
      </div>

      {/* Full pricing section — same as public site */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
        </div>
      ) : (
        <PricingSection
          tiers={tiers}
          countryName={countryData.name}
          currency={countryData.currency}
          currencySymbol={countryData.currencySymbol}
          currencyPosition={countryData.currencyPosition}
          onSelectPlan={handleSelectPlan}
          compact={compact}
          // Clinic plans are billed monthly (decision 2).
          hideAnnualToggle={category === "facility"}
        />
      )}
    </div>
  );
}
